import type { ComponentProps } from "react";
import { useLocation, useSearchParams } from "react-router";
import { LeadsTable } from "@/components/leads-table";
import { LeadsSkeleton } from "@/components/page-skeletons";
import { ViewFallback } from "@/components/page-states";
import { WorkspaceBanner } from "@/components/workspace-banner";
import { OfferLinkGate } from "@/components/offer-link-gate";
import { usePageTitle, useView } from "@shared/client/view";
import { useT } from "@shared/client/locale";
import { LEAD_STATUSES, type LeadStatus } from "@shared/lib/leads-shared";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type LeadsData = Pick<ComponentProps<typeof LeadsTable>, "leads" | "loadError"> & {
  view: { clientName: string } | null;
};

/**
 * L'écran de travail. Il montre le pipeline de l'offre consultée — plus les
 * leads qui sont assignés à l'utilisateur, d'où qu'ils viennent. Il accepte
 * trois liens profonds, pour que les autres écrans puissent y amener
 * précisément : `?filter=new` (la carte de veille envoie sur les leads non
 * traités), `?lead=<id>` (un rapport ouvre la fiche qu'il a produite ;
 * `&tab=message` l'ouvre sur ses messages) et `?offer=<id>` (le
 * récapitulatif hebdomadaire fait d'abord consulter l'offre, voir `LeadsPage`).
 */
function LeadsScreen() {
  const t = useT();
  usePageTitle(t.leads.page.title);
  const [params] = useSearchParams();
  const { key: linkKey } = useLocation();
  const { data, error } = useView<LeadsData>("/api/views/leads");
  if (!data) return <ViewFallback error={error} skeleton={<LeadsSkeleton />} subject={t.leads.page.subject} />;

  const filter = params.get("filter");
  const lead = params.get("lead");
  const initialStatus: "all" | LeadStatus =
    filter && LEAD_STATUSES.includes(filter as LeadStatus) ? (filter as LeadStatus) : "all";
  const initialLeadId = lead && UUID_PATTERN.test(lead) ? lead : null;
  // `&tab=message` : le « Ouvrir » d'un toast « Messages prêts » mène aux brouillons.
  const initialTab = initialLeadId && params.get("tab") === "message" ? "message" : undefined;

  return (
    <>
      {data.view && (
        <WorkspaceBanner>
          {t.leads.page.workspaceBanner.before}
          <strong>{data.view.clientName}</strong>
          {t.leads.page.workspaceBanner.after}
        </WorkspaceBanner>
      )}
      <LeadsTable
        initialLeadId={initialLeadId}
        initialStatus={initialStatus}
        initialTab={initialTab}
        leads={data.leads}
        linkKey={linkKey}
        loadError={data.loadError}
        readOnly={Boolean(data.view)}
      />
    </>
  );
}

/** `?offer=<id>` : l'offre devient celle qu'on consulte avant que l'écran ne lise sa vue. */
export default function LeadsPage() {
  return (
    <OfferLinkGate fallback={<LeadsSkeleton />}>
      <LeadsScreen />
    </OfferLinkGate>
  );
}
